// src/components/common/ImageSelectionModal.js
import React, { useState, useEffect } from "react";
import { Modal, Button, Row, Col, Card, Spinner, Form } from "react-bootstrap";
import { useData } from "../../providers/dataProvider";
import "./ImageSelectionModal.css";

/**
 * Reusable component for selecting images from S3 bucket
 * Uses the DataProvider for accessing images module functionality
 *
 * @param {boolean} show - Controls visibility of the modal
 * @param {function} onHide - Function to call when modal is closed
 * @param {function} onSelect - Function to call when an image is selected, receives the image path
 * @param {string} currentImage - The currently selected image path (if any)
 */
const ImageSelectionModal = ({ show, onHide, onSelect, currentImage }) => {
  // Get images data and functions from the DataProvider
  const { images, imagesLoading, refreshImages, getImageUrl } = useData();

  const [selectedImage, setSelectedImage] = useState(currentImage || "");
  const [searchTerm, setSearchTerm] = useState("");
  const [imageUrlMap, setImageUrlMap] = useState({});
  const [error, setError] = useState(null);

  // When a new image is selected, update the state
  const handleSelectImage = (imageKey) => {
    setSelectedImage(imageKey);
  };

  // When the select button is clicked, call the onSelect callback
  const handleConfirmSelection = () => {
    onSelect(selectedImage);
    onHide();
  };

  // Load image URLs for display
  useEffect(() => {
    if (!show || imagesLoading) return;

    const loadImageUrls = async () => {
      try {
        const urlMap = {};

        // Process each image to get its display URL
        for (const image of images) {
          if (image.aws_location) {
            // Use the direct S3 URL for display
            const url = getImageUrl(image.aws_location);
            if (url) {
              urlMap[image.aws_location] = url;
            }
          }
        }

        setImageUrlMap(urlMap);
        setError(null);
      } catch (err) {
        console.error("Error loading image URLs:", err);
        setError("Failed to load image URLs. Please try again.");
      }
    };

    loadImageUrls();
  }, [show, images, imagesLoading, getImageUrl]);

  // Reset the selected image when the currentImage prop changes
  useEffect(() => {
    setSelectedImage(currentImage || "");
  }, [currentImage]);

  // Filter images based on search term
  const filteredImages = images?.filter((image) => {
    const description = image.description || "";
    const altText = image.alt_text || "";
    const fileName = image.aws_location?.split("/").pop() || "";

    const searchTermLower = searchTerm.toLowerCase();
    return (
      description.toLowerCase().includes(searchTermLower) ||
      altText.toLowerCase().includes(searchTermLower) ||
      fileName.toLowerCase().includes(searchTermLower)
    );
  });

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="image-selection-modal"
    >
      <Modal.Header closeButton className="bg-dark text-white border-secondary">
        <Modal.Title>Select Image</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-white">
        <Form.Group className="mb-3">
          <Form.Control
            type="text"
            placeholder="Search images..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-dark text-light border-secondary"
          />
        </Form.Group>

        {imagesLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="light" />
            <p className="mt-2">Loading images...</p>
          </div>
        ) : error ? (
          <div className="text-center py-5">
            <p className="text-danger">{error}</p>
            <Button variant="primary" onClick={refreshImages}>
              Retry
            </Button>
          </div>
        ) : (
          <Row className="image-grid g-3">
            {filteredImages?.length > 0 ? (
              filteredImages?.map((image) => (
                <Col xs={6} md={4} lg={3} key={image.id}>
                  <Card
                    className={`image-card h-100 cursor-pointer ${
                      selectedImage === image.aws_location
                        ? "border-primary"
                        : "border-secondary"
                    }`}
                    style={{ width: "100%" }}
                    onClick={() => handleSelectImage(image.aws_location)}
                  >
                    {imageUrlMap[image.aws_location] ? (
                      <Card.Img
                        variant="top"
                        src={imageUrlMap[image.aws_location]}
                        alt={image.alt_text || "Image"}
                        style={{ height: "120px", objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        className="image-placeholder d-flex align-items-center justify-content-center"
                        style={{ height: "120px" }}
                      >
                        <Spinner animation="border" size="sm" />
                      </div>
                    )}
                    <Card.Body className="p-2 bg-dark text-white">
                      <small className="d-block text-truncate">
                        {image.description ||
                          image.aws_location?.split("/").pop() ||
                          "Image"}
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            ) : (
              <Col xs={12}>
                <p className="text-center py-4">
                  {searchTerm
                    ? "No images match your search"
                    : "No images available"}
                </p>
              </Col>
            )}
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-dark text-white border-secondary">
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirmSelection}
          disabled={!selectedImage}
        >
          Select Image
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageSelectionModal;
